/**
 * The period every report is read over.
 *
 * Six presets and a custom window, resolved to the two ISO instants the API
 * filters on. The presets are not a convenience — they are the answer to "what
 * did yesterday do", which is the question almost every one of these reports is
 * opened to settle, and making somebody pick two dates to ask it is how a daily
 * habit becomes a chore.
 *
 * BOUNDARIES ARE LOCAL, NOT UTC. A day starts at midnight where the business
 * trades, so a sale at 00:30 belongs to the day that has just started rather
 * than to the one the server's UTC clock is still in. The browser's own zone is
 * what decides that here, and `reportTimeZone()` names it on screen so a report
 * read from another country says so rather than quietly shifting by a few hours.
 */

export const ReportRange = {
  Today: 'today',
  Yesterday: 'yesterday',
  Last7: '7d',
  Last30: '30d',
  ThisMonth: 'this-month',
  LastMonth: 'last-month',
  Custom: 'custom',
} as const

export type ReportRange = (typeof ReportRange)[keyof typeof ReportRange]

/** The pills, in the order they are read: narrowest first, then the months. */
export const REPORT_RANGE_OPTIONS: { id: ReportRange; label: string }[] = [
  { id: ReportRange.Today, label: 'Today' },
  { id: ReportRange.Yesterday, label: 'Yesterday' },
  { id: ReportRange.Last7, label: 'Last 7' },
  { id: ReportRange.Last30, label: 'Last 30' },
  { id: ReportRange.ThisMonth, label: 'This month' },
  { id: ReportRange.LastMonth, label: 'Last month' },
  { id: ReportRange.Custom, label: 'Custom' },
]

/** A custom window, as the two `<input type="date">` values holding it. */
export interface CustomRange {
  /** `YYYY-MM-DD`, inclusive. */
  from: string
  /** `YYYY-MM-DD`, inclusive — the whole of this day counts. */
  to: string
}

/** What the API is actually sent. Both ISO instants; `to` is exclusive. */
export interface ResolvedRange {
  from: string
  to: string
  /** `1 – 31 Aug 2026`. What the meta line says. */
  label: string
}

/**
 * The window a preset means, as instants.
 *
 * `to` is the START of the day after the last one included, never 23:59:59.
 * A sale at 23:59:30.500 is inside the day it happened on, and an end bound of
 * `23:59:59` drops it — the kind of error that loses three tickets a month and
 * is never traced, because the total is only ever slightly wrong.
 */
export function resolveRange(range: ReportRange, custom: CustomRange): ResolvedRange {
  const start = startOfToday()

  switch (range) {
    case ReportRange.Today:
      return window(start, addDays(start, 1))

    case ReportRange.Yesterday:
      return window(addDays(start, -1), start)

    // Seven days INCLUDING today, not seven before it. "Last 7" beside a
    // "Today" pill that already exists reads as the week you are in.
    case ReportRange.Last7:
      return window(addDays(start, -6), addDays(start, 1))

    case ReportRange.Last30:
      return window(addDays(start, -29), addDays(start, 1))

    case ReportRange.ThisMonth:
      return window(startOfMonth(start, 0), addDays(start, 1))

    case ReportRange.LastMonth:
      return window(startOfMonth(start, -1), startOfMonth(start, 0))

    case ReportRange.Custom:
      return customWindow(custom)
  }
}

/** Today, as both ends of a custom range — what the date inputs open on. */
export function todayAsCustomRange(): CustomRange {
  const today = isoDate(startOfToday())

  return { from: today, to: today }
}

/**
 * The zone the boundaries were computed in.
 *
 * Shown rather than assumed. The books are kept in `Asia/Phnom_Penh`, and a
 * manager reading these from a laptop still set to another zone is looking at
 * days that begin at the wrong hour — better that the screen says which zone it
 * used than that the figures quietly disagree with the till's.
 */
export function reportTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
}

/** The zone the SERVER files a trading day under. See `REPORT_TIMEZONE`. */
export const BOOKS_TIME_ZONE = 'Asia/Phnom_Penh'

/** A period as trading days — what the sales reports are asked for. */
export interface TradingDays {
  /** `YYYY-MM-DD`, inclusive. */
  from: string
  /** `YYYY-MM-DD`, inclusive. */
  to: string
  /** `1 – 31 Aug 2026`. */
  label: string
}

/**
 * The window a preset means, as Phnom Penh calendar days.
 *
 * The counterpart to `resolveRange` for the reports that take DAYS rather than
 * instants. Those resolve the day boundaries on the server, in the books' zone,
 * so "today" here has to be the books' today as well — not the browser's. A
 * manager opening "Yesterday" from Bangkok at 23:30 is still asking about the
 * day the outlet has just closed, and the browser's own date would hand them
 * the one before it.
 *
 * The arithmetic is done on calendar dates in UTC, where there is no daylight
 * saving to move a midnight about; only "what is today" depends on a zone.
 */
export function resolveTradingDays(range: ReportRange, custom: CustomRange): TradingDays {
  const today = booksToday()

  switch (range) {
    case ReportRange.Today:
      return days(today, today)

    case ReportRange.Yesterday: {
      const yesterday = shiftDay(today, -1)

      return days(yesterday, yesterday)
    }

    // Including today, for the same reason as `resolveRange`.
    case ReportRange.Last7:
      return days(shiftDay(today, -6), today)

    case ReportRange.Last30:
      return days(shiftDay(today, -29), today)

    case ReportRange.ThisMonth:
      return days(`${today.slice(0, 7)}-01`, today)

    case ReportRange.LastMonth: {
      const lastOfPrevious = shiftDay(`${today.slice(0, 7)}-01`, -1)

      return days(`${lastOfPrevious.slice(0, 7)}-01`, lastOfPrevious)
    }

    case ReportRange.Custom: {
      const a = DAY.test(custom.from) ? custom.from : today
      const b = DAY.test(custom.to) ? custom.to : a

      // Swapped rather than refused — see `customWindow`.
      return a <= b ? days(a, b) : days(b, a)
    }
  }
}

/** Today in the books' zone, as `YYYY-MM-DD`. Also what the date inputs open on. */
export function booksToday(at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BOOKS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? ''

  return `${part('year')}-${part('month')}-${part('day')}`
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

function days(from: string, to: string): TradingDays {
  return { from, to, label: dayRangeLabel(from, to) }
}

function shiftDay(day: string, by: number): string {
  const at = utcDay(day)

  at.setUTCDate(at.getUTCDate() + by)

  return at.toISOString().slice(0, 10)
}

function utcDay(day: string): Date {
  const [year, month, date] = day.split('-').map(Number)

  return new Date(Date.UTC(year, month - 1, date))
}

/**
 * `1 – 31 Aug 2026` for two calendar days.
 *
 * Formatted in UTC because that is the zone `utcDay` built them in — formatting
 * a calendar date in the browser's zone is how "1 Aug" prints as "31 Jul" on a
 * machine west of Greenwich.
 */
function dayRangeLabel(from: string, to: string): string {
  const first = utcDay(from)
  const last = utcDay(to)
  const full: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }

  if (from === to) return first.toLocaleDateString(undefined, full)

  const sameYear = from.slice(0, 4) === to.slice(0, 4)
  const start =
    from.slice(0, 7) === to.slice(0, 7)
      ? String(first.getUTCDate())
      : first.toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC',
          ...(sameYear ? {} : { year: 'numeric' }),
        })

  return `${start} – ${last.toLocaleDateString(undefined, full)}`
}

/* -------------------------------------------------------------------------- */
/* Internals                                                                  */
/* -------------------------------------------------------------------------- */

function window(from: Date, toExclusive: Date): ResolvedRange {
  return {
    from: from.toISOString(),
    to: toExclusive.toISOString(),
    label: rangeLabel(from, addDays(toExclusive, -1)),
  }
}

/**
 * A custom window, with the two dates put back in order if they were entered
 * backwards.
 *
 * Swapping beats refusing: somebody who typed the end date first meant a range,
 * and an empty table with no explanation is a worse answer than the range they
 * obviously wanted.
 */
function customWindow({ from, to }: CustomRange): ResolvedRange {
  const a = startOfLocalDay(from) ?? startOfToday()
  const b = startOfLocalDay(to) ?? a
  const [first, last] = a <= b ? [a, b] : [b, a]

  return {
    from: first.toISOString(),
    to: addDays(last, 1).toISOString(),
    label: rangeLabel(first, last),
  }
}

/** `1 – 31 Aug 2026`, collapsing to one date when the window is a single day. */
function rangeLabel(from: Date, lastIncluded: Date): string {
  const sameDay = isoDate(from) === isoDate(lastIncluded)
  const full: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

  if (sameDay) return from.toLocaleDateString(undefined, full)

  const sameMonth =
    from.getFullYear() === lastIncluded.getFullYear() && from.getMonth() === lastIncluded.getMonth()

  const start = sameMonth
    ? String(from.getDate())
    : from.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        ...(from.getFullYear() === lastIncluded.getFullYear() ? {} : { year: 'numeric' }),
      })

  return `${start} – ${lastIncluded.toLocaleDateString(undefined, full)}`
}

function startOfToday(): Date {
  const now = new Date()

  now.setHours(0, 0, 0, 0)

  return now
}

function startOfMonth(from: Date, monthsBack: number): Date {
  return new Date(from.getFullYear(), from.getMonth() + monthsBack, 1)
}

function addDays(from: Date, days: number): Date {
  const next = new Date(from)

  next.setDate(next.getDate() + days)

  return next
}

/**
 * `YYYY-MM-DD` at local midnight.
 *
 * Built from the parts rather than `new Date('2026-08-01')`, which the spec
 * reads as UTC midnight — in Phnom Penh that is 07:00 on the 1st, so a custom
 * range would silently start seven hours late.
 */
function startOfLocalDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (!match) return null

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/** Local `YYYY-MM-DD` — what an `<input type="date">` holds. */
function isoDate(at: Date): string {
  const month = String(at.getMonth() + 1).padStart(2, '0')
  const day = String(at.getDate()).padStart(2, '0')

  return `${at.getFullYear()}-${month}-${day}`
}
